import z from 'zod';
import { isValidUsZipCode } from '@/app_lib/utils/utils';

// biome-ignore-start lint/style/useNamingConvention: <all external data>
export const RatesData = z.object({
  advertiser_notes: z.string(),
  apy: z.number(),
  effective_at: z.string(),
  ep_url: z.string(),
  institution: z.object({
    insurance_information: z.object({
      disclosure: z.string(),
      type: z.string(),
    }),
    logo_url: z.string(),
    name: z.string(),
    reviews: z.object({
      link: z.string().nullable(),
      overall_rating: z.number().nullable(),
      short_summary: z.string().nullable(),
      website_url: z.string().nullable(),
    }),
  }),
  is_local: z.boolean(),
  is_national: z.boolean(),
  is_paid: z.boolean(),
  max_to_open: z.number().nullable(),
  min_to_open: z.number(),
  pixel_url: z.string(),
  product: z.object({
    name: z.string(),
    term: z.number().optional(),
    type: z.string(),
  }),
  rate: z.number(),
});
// biome-ignore-end lint/style/useNamingConvention: <>

export type RatesData = z.infer<typeof RatesData>;

// biome-ignore-start lint/style/useNamingConvention: <all external data>
export const CdRatesResponse = z.object({
  cd_rates: z.array(RatesData),
});
// biome-ignore-end lint/style/useNamingConvention: <>

export type CdRatesResponse = z.infer<typeof CdRatesResponse>;

// biome-ignore-start lint/style/useNamingConvention: <all external data>
export const SavingsMmaResponse = z.object({
  savings_mma_rates: z.array(RatesData),
});
// biome-ignore-end lint/style/useNamingConvention: <>

export type SavingsMmaResponse = z.infer<typeof SavingsMmaResponse>;

export const FilterBy = z.object({
  accountType: z.string(),
  depositAmount: z.number(),
  maxTermLength: z.number(),
  minTermLength: z.number(),
  zip: z.string().refine(isValidUsZipCode, { message: 'Invalid US zip code' }),
});

export type FilterBy = z.infer<typeof FilterBy>;

export const Widget = z.object({
  data: z.string().optional(),
  databaseId: z.number().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
});

export const Taxonomy = z.object({
  order: z.number(),
  position: z.union([z.literal('header'), z.literal('in_stream'), z.literal('right_side'), z.literal('footer')]),
  term: z.string(),
});

export type Taxonomy = z.infer<typeof Taxonomy>;

export const WidgetComponent = z.object({
  components: z.array(Widget),
  slug: z.string(),
  taxonomies: z.array(Taxonomy).nullable(),
  templateSlug: z.string(),
});

export type WidgetComponent = z.infer<typeof WidgetComponent>;

export const WidgetResponse = z.object({
  widgets: z.object({
    data: z.array(WidgetComponent).default([]),
  }),
});

// biome-ignore-start lint/style/useNamingConvention: <all external data>
export const EducationWidgetFeatures = z
  .object({
    columns: z.number(),
    data: z.record(z.string(), z.array(z.string())),
    has_footer: z.number(),
    has_header: z.number(),
    rows: z.number(),
  })
  .optional();
// biome-ignore-end lint/style/useNamingConvention: <>

export type EducationWidgetFeatures = z.infer<typeof EducationWidgetFeatures>;

export const WidgetData = z.array(WidgetComponent);

export type WidgetResponse = z.infer<typeof WidgetResponse>;

export type WidgetData = z.infer<typeof WidgetData>;

export type Widget = z.infer<typeof Widget>;

export type WidgetPlacement = Map<Taxonomy['position'] | null, WidgetComponent[]>;

export type WidgetBuilder = ReturnType<typeof WidgetBuilder>;

// biome-ignore lint/style/useNamingConvention: naming scheme makes sense for this builder since it's a Map
export function WidgetBuilder(widgetData: WidgetData) {
  return {
    build: <T>(index = 0) => {
      const components = widgetData[index]?.components || [];
      const componentFields = components || [];
      const map = componentFields.reduce((acc, field) => {
        if (field.name) {
          return acc.set(field.name as T, field.data);
        }
        return acc;
      }, new Map<T, string | undefined>());

      return {
        get: (name: T) => map.get(name),
      };
    },
    buildMeta: <T>(index = 0) => {
      const response = widgetData || [];
      const data = {
        slug: response[index]?.slug || '',
        templateSlug: response[index]?.templateSlug || '',
      };
      return {
        get: (name: T) => data[name as keyof typeof data],
      };
    },
  };
}

export function generateWidgetPlacement(widgetResponse: WidgetResponse | null): WidgetPlacement {
  const map: WidgetPlacement = new Map<Taxonomy['position'] | null, WidgetComponent[]>();
  if (!widgetResponse) {
    return map;
  }
  const widgets = widgetResponse.widgets.data || [];

  for (const widget of widgets) {
    if (!widget.taxonomies) {
      continue;
    }
    const taxonomy = widget.taxonomies[0];
    const position = taxonomy?.position ?? null;

    if (position !== undefined) {
      map.set(position, (map.get(position) || []).concat(widget));
    }
  }
  return map;
}

export function parseNestedFields<T>(data: string | undefined) {
  if (!data) {
    return null;
  }
  try {
    const parsed = JSON.parse(data) as T;
    return parsed;
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: need error
    console.error('Error parsing widget data', error);
    return null;
  }
}
